import { Box, Flex, Grid, Heading, Icon, Link, Text, VStack } from '@chakra-ui/react';
import { FaCheckCircle, FaMapMarkerAlt, FaUsers, FaCreditCard, FaArrowUp } from 'react-icons/fa';
import { headingFont } from 'theme/tokens';
import { translate } from 'i18n';
import RoiCalculator from '../components/RoiCalculator';
import WhatsAppCta from '../components/WhatsAppCta';

function CaseStudyStat({
  icon,
  value,
  label,
}: {
  icon: React.ElementType;
  value: string;
  label: string;
}) {
  return (
    <VStack spacing={1}>
      <Icon as={icon} color="accent.400" boxSize={5} />
      <Text fontFamily={headingFont} fontWeight="800" fontSize="2xl" color="gray.800">
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500" textAlign="center">
        {label}
      </Text>
    </VStack>
  );
}

export default function RoiCalculatorPage() {
  return (
    <Box>
      {/* Hero section */}
      <Box
        bg="linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f0fdf4 100%)"
        px={{ base: 4, md: 8 }}
        pt={{ base: 12, md: 20 }}
        pb={{ base: 8, md: 14 }}
      >
        <VStack maxW="700px" mx="auto" spacing={5} textAlign="center">
          <Heading
            fontFamily={headingFont}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="800"
            lineHeight="1.1"
            letterSpacing="-0.03em"
            color="gray.900"
          >
            {translate('marketing.calc_hero_title')}
          </Heading>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600" maxW="540px">
            {translate('marketing.calc_hero_subtitle')}
          </Text>
        </VStack>
      </Box>

      {/* Calculator */}
      <Box px={{ base: 4, md: 8 }} py={{ base: 8, md: 14 }}>
        <RoiCalculator />
      </Box>

      {/* Social proof + case study */}
      <Box bg="gray.50" px={{ base: 4, md: 8 }} py={14}>
        <VStack maxW="900px" mx="auto" spacing={10}>
          {/* Headline */}
          <VStack spacing={3} textAlign="center">
            <Text fontWeight="700" fontSize="xl" color="gray.800">
              {translate('marketing.calc_social_proof_title')}
            </Text>
            <Text fontSize="sm" color="gray.500" maxW="540px">
              {translate('marketing.calc_social_proof_body')}
            </Text>
          </VStack>

          {/* Frejus case study card */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            p={{ base: 6, md: 10 }}
            w="100%"
            maxW="700px"
            shadow="sm"
          >
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              gap={4}
              mb={6}
            >
              <Box>
                <Text
                  fontFamily={headingFont}
                  fontWeight="800"
                  fontSize="lg"
                  color="gray.800"
                >
                  {translate('marketing.case_study_title')}
                </Text>
                <Flex align="center" gap={1} mt={1}>
                  <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3} />
                  <Text fontSize="sm" color="gray.500">
                    {translate('marketing.case_study_location')}
                  </Text>
                </Flex>
              </Box>
            </Flex>

            <Text fontSize="sm" color="gray.600" lineHeight="1.7" mb={6}>
              {translate('marketing.case_study_body')}
            </Text>

            <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={6}>
              <CaseStudyStat
                icon={FaCheckCircle}
                value={translate('marketing.case_study_stat1_value')}
                label={translate('marketing.case_study_stat1_label')}
              />
              <CaseStudyStat
                icon={FaUsers}
                value={translate('marketing.case_study_stat2_value')}
                label={translate('marketing.case_study_stat2_label')}
              />
              <CaseStudyStat
                icon={FaCreditCard}
                value={translate('marketing.case_study_stat3_value')}
                label={translate('marketing.case_study_stat3_label')}
              />
              <CaseStudyStat
                icon={FaCheckCircle}
                value={translate('marketing.case_study_stat4_value')}
                label={translate('marketing.case_study_stat4_label')}
              />
            </Grid>

            <Link
              onClick={() => {
                // Try the specific entry first, fall back to the methodology wrapper
                const target =
                  document.getElementById('method-validation') ??
                  document.getElementById('roi-methodology');
                target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              fontSize="xs"
              color="brand.500"
              fontWeight="500"
              mt={4}
              display="inline-flex"
              alignItems="center"
              gap={1}
              cursor="pointer"
              _hover={{ color: 'brand.600', textDecoration: 'underline' }}
            >
              <Icon as={FaArrowUp} boxSize={2.5} />
              {translate('marketing.case_study_see_methodology')}
            </Link>
          </Box>
        </VStack>
      </Box>

      {/* WhatsApp CTA strip */}
      <Box px={{ base: 4, md: 8 }} py={14} textAlign="center">
        <VStack spacing={4}>
          <Text fontWeight="600" fontSize="lg" color="gray.700">
            {translate('marketing.calc_bottom_cta')}
          </Text>
          <WhatsAppCta />
        </VStack>
      </Box>
    </Box>
  );
}
